import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import Chip from "@material-ui/core/Chip";
import CircularProgress from "@material-ui/core/CircularProgress";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import BlockIcon from "@material-ui/icons/Block";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import HistoryIcon from "@material-ui/icons/History";
import Tooltip from "@material-ui/core/Tooltip";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

import api from "../../services/api";
import AutoReplyRuleModal from "../../components/AutoReplyRuleModal";
import AutoReplyRuleLogsModal from "../../components/AutoReplyRuleLogsModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_RULES":
      return action.payload;
    case "UPDATE_RULES": {
      const rule = action.payload;
      const idx = state.findIndex(r => r.id === rule.id);
      if (idx !== -1) {
        const newState = [...state];
        newState[idx] = rule;
        return newState;
      }
      return [rule, ...state];
    }
    case "DELETE_RULE":
      return state.filter(r => r.id !== action.payload);
    default:
      return state;
  }
};

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(3),
  },
  replyPreview: {
    maxWidth: 320,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "block",
  },
}));

const AutoReplyRules = () => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [rules, dispatch] = useReducer(reducer, []);
  const [selectedRule, setSelectedRule] = useState(null);
  const [deletingRule, setDeletingRule] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [ruleModalOpen, setRuleModalOpen] = useState(false);
  const [logsModalOpen, setLogsModalOpen] = useState(false);
  const [logsRule, setLogsRule] = useState(null);

  const fetchRules = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auto-reply-rules");
      dispatch({ type: "LOAD_RULES", payload: data });
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const companyId = user.companyId;
    const onEvent = data => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_RULES", payload: data.rule });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_RULE", payload: +data.id });
      }
    };
    socket.on(`company-${companyId}-autoReplyRule`, onEvent);
    return () => {
      socket.off(`company-${companyId}-autoReplyRule`, onEvent);
    };
  }, [socket, user.companyId]);

  const handleOpenModal = () => {
    setSelectedRule(null);
    setRuleModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedRule(null);
    setRuleModalOpen(false);
    fetchRules();
  };

  const handleEditRule = rule => {
    setSelectedRule(rule);
    setRuleModalOpen(true);
  };

  const handleOpenLogs = rule => {
    setLogsRule(rule);
    setLogsModalOpen(true);
  };

  const handleDeleteRule = async id => {
    try {
      await api.delete(`/auto-reply-rules/${id}`);
      toast.success("Regra excluída com sucesso!");
    } catch (err) {
      toastError(err);
    }
    setDeletingRule(null);
    fetchRules();
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={deletingRule && `Excluir a regra "${deletingRule.name}"?`}
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteRule(deletingRule.id)}
      >
        Essa ação é irreversível.
      </ConfirmationModal>
      <AutoReplyRuleModal
        open={ruleModalOpen}
        onClose={handleCloseModal}
        ruleId={selectedRule && selectedRule.id}
      />
      <AutoReplyRuleLogsModal
        open={logsModalOpen}
        onClose={() => setLogsModalOpen(false)}
        rule={logsRule}
      />
      <MainHeader>
        <Title>Resposta Automática ({rules.length})</Title>
        <MainHeaderButtonsWrapper>
          <Button variant="contained" color="primary" onClick={handleOpenModal}>
            Adicionar regra
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Palavras / frases</TableCell>
              <TableCell>Mensagem de resposta</TableCell>
              <TableCell align="center">Bloqueia?</TableCell>
              <TableCell align="center">Ativa</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rules.map(rule => (
              <TableRow key={rule.id} hover>
                <TableCell>{rule.name}</TableCell>
                <TableCell>
                  {(rule.keywords || []).map((kw, i) => (
                    <Chip key={i} label={kw} size="small" style={{ marginRight: 4, marginBottom: 4 }} />
                  ))}
                </TableCell>
                <TableCell>
                  <Tooltip title={rule.replyMessage || ""}>
                    <span className={classes.replyPreview}>{rule.replyMessage}</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="center">
                  {rule.blockContact && <BlockIcon style={{ color: "#c62828", fontSize: 18 }} />}
                </TableCell>
                <TableCell align="center">
                  {rule.active ? (
                    <CheckCircleIcon style={{ color: "green", fontSize: 18 }} />
                  ) : (
                    <CancelIcon style={{ color: "red", fontSize: 18 }} />
                  )}
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Ver histórico de disparos">
                    <IconButton size="small" onClick={() => handleOpenLogs(rule)}>
                      <HistoryIcon />
                    </IconButton>
                  </Tooltip>
                  <IconButton size="small" onClick={() => handleEditRule(rule)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setConfirmModalOpen(true);
                      setDeletingRule(rule);
                    }}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {loading && (
          <div className={classes.loadingContainer}>
            <CircularProgress />
          </div>
        )}
      </Paper>
    </MainContainer>
  );
};

export default AutoReplyRules;
